import { GapFillStatusEnum } from "@app/projects/network-assessment/adequacy";
import { GapManagementDashboardService } from "@app/projects/network-assessment/gap-management-dashboard/gapManagementDashboardService";
import { presentableGapFillStatusRecord } from "@app/projects/network-assessment/gap-management-dashboard/helpers/presentableGapFillStatusRecord";
import { getFillableBadge, getGapTypeBadge, getPriorityBadge } from "@app/projects/network-assessment/gap-management-dashboard/helpers/gmdGapBadges";
import { GmdServicingProviderSubset } from "@app/projects/network-assessment/gap-management-dashboard/interfaces/gapFlyoutResponseModel";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { FC, JSX, useEffect, useMemo, useRef, useState } from "react";
import { QesColors } from "@app/shared/qes-standards/colors";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { useQuery } from "@tanstack/react-query";

export interface GmdGapFlyoutProps {
  projectId: number;
  regionSetSpecialtyGroupId: number;
  runConfigId: number;
  specialty: string;
}

enum ProvidersView {
  Network,
  Impact,
}

interface GmdProvidersTable {
  [wafflesKeyColumnName]: string;
  gains: JSX.Element;
  impact: string;
  providerData: JSX.Element;
  serves: string;
}

const formatDate = (date: Date) => {
  if (isNaN(date.getDate())) {
    return null;
  }

  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  return `${date.toLocaleString("default", { month: "long" })} ${date.getDate()}, ${date.getFullYear()}`;
}
export const GmdGapFlyout: FC<GmdGapFlyoutProps> = (props: GmdGapFlyoutProps) => {
  const gapManagementDashboardService = useRef(new GapManagementDashboardService()).current;
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [tempResolutionStatus, setTempResolutionStatus] = useState<GapFillStatusEnum>();
  const [tempDateDue, setTempDateDue] = useState<string>();
  const [providersView, setProvidersView] = useState<ProvidersView>(ProvidersView.Network);
  const [networkProvidersData, setNetworkProvidersData] = useState<GmdProvidersTable[]>([]);
  const [impactProvidersData, setImpactProvidersData] = useState<GmdProvidersTable[]>([]);
  const [fillabilityBadge, setFillabilityBadge] = useState<JSX.Element>();
  const [priorityBadge, setPriorityBadge] = useState<JSX.Element>();
  const [typeBadge, setTypeBadge] = useState<JSX.Element>();
  // mock data
  const coverageScore = 34;
  const marketPotential = 52;

  const providersColumnConfig: ColumnConfig = useMemo(() => {
    return {
      gains: {
        alignment: TextAlignment.Right,
        displayText: "Gains %",
        hidden: providersView === ProvidersView.Network,
        sizeContentToFit: true,
        testId: "gains-percent",
      },
      impact: {
        alignment: TextAlignment.Right,
        displayText: "Impact %",
        hidden: providersView === ProvidersView.Network,
        sizeContentToFit: true,
        testId: "impact-percent",
      },
      providerData: {
        displayText: "Provider",
        sizeContentToFit: true,
        testId: "provider-data",
      },
      serves: {
        alignment: TextAlignment.Right,
        displayText: "Serves %",
        sizeContentToFit: true,
        testId: "serves-percent",
      },
    }
  }, [providersView]);

  const resolutionStatusActionMenuItems = useMemo(() => {
    return Object.entries(GapFillStatusEnum).map(([key, value]) => {
      const isActive = value === tempResolutionStatus;
      return (
        <NewActionMenuItem
          key={key}
          display={<>{presentableGapFillStatusRecord[value]} {isActive ? <QesIcon iconName={QesIcons.Check} isDecorative /> : ""}</>}
          onClick={() => setTempResolutionStatus(value)}
          testId={`gmd-gap-flyout__resolution-status-option--${key}`}
          className={isActive ? "gmd-gap-flyout__resolution-status-option--active" : ""}
        />
      );
    });
  }, [tempResolutionStatus]);

  const buildTableRows = (data: GmdServicingProviderSubset[]) => {
    const formatGainsPercent = (gainsPercent: number) => {
      if (gainsPercent > 0) {
        return <span key={gainsPercent} className={"text-success"}>+{gainsPercent.toFixed(1)}</span>;
      } else if (gainsPercent < 0) {
        return <span key={gainsPercent} className={"text-danger"}>{gainsPercent.toFixed(1)}</span>
      } else {
        return <span key={gainsPercent}>{gainsPercent.toFixed(1)}</span>;
      }
    }
    /* oxlint-disable sort-keys */
    return data.map((d) => ({
      [wafflesKeyColumnName]: d.npi || d.name,
      providerData: <div key={d.name}>
        <p className="gmd-gap-flyout__provider-name">{d.name}</p>
        {!!d.npi && <p className="gmd-gap-flyout__npi">NPI: {d.npi}</p>}
      </div>,
      serves: d.servesPercent.toFixed(1),
      gains: d.gainsPercent !== undefined ? formatGainsPercent(d.gainsPercent) : <>---</>,
      impact: d.impactsPercent !== undefined ? d.impactsPercent.toFixed(1) : "---",
    }));
    /* oxlint-enable */
  }

  const customSort = (data: GmdProvidersTable[], column: keyof GmdProvidersTable, direction: SortDirections) => {
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    const sortedData = [...data];
    switch (column) {
      case "impact":
      case "serves":
        return numberSort(data, column, multiplier);
      case "gains":
        return numberSort(data, column, multiplier, (item) => (item as JSX.Element).key);
      case "providerData":
        return sortedData.sort((a, b) => (a[column].key > b[column].key ? 1 : -1) * multiplier);
    }
  }

  const resolutionStatusColorRecord: Record<GapFillStatusEnum, string> = {
    [GapFillStatusEnum.none]: "",
    [GapFillStatusEnum.gapFilled]: "text-success",
    [GapFillStatusEnum.researching]: "text-info",
    [GapFillStatusEnum.contractingGapClosure]: "text-info",
    [GapFillStatusEnum.contractingExceptionEligible]: "text-info",
    [GapFillStatusEnum.exceptionRequired]: "text-danger",
    [GapFillStatusEnum.rppoWaiverRequired]: "text-danger",
    [GapFillStatusEnum.availableEntitiesWillNotContract]: "text-danger"
  }

  const { data: gmdFlyoutResponse, isError, isLoading } = useQuery({
    enabled: !!props.projectId && !!props.runConfigId && !!props.regionSetSpecialtyGroupId,
    queryFn: ({ signal }) => gapManagementDashboardService.getGapProviderFlyoutData(props.projectId, props.runConfigId, props.regionSetSpecialtyGroupId, signal),
    queryKey: ["gmd-gap-flyout", props.runConfigId, props.regionSetSpecialtyGroupId],
  });

  // massage api response data into JSX 
  useEffect(() => {
    if (!!gmdFlyoutResponse) {
      setNetworkProvidersData(buildTableRows(gmdFlyoutResponse.contractedProviders));
      setImpactProvidersData(buildTableRows(gmdFlyoutResponse.availableProviders));
      setFillabilityBadge(getFillableBadge(gmdFlyoutResponse.fillable));
      setTempDateDue(gmdFlyoutResponse.gapDueDate);
      setPriorityBadge(getPriorityBadge(gmdFlyoutResponse.gapPriority));
      setTypeBadge(getGapTypeBadge(gmdFlyoutResponse.gapType));
      setTempResolutionStatus(gmdFlyoutResponse.gapFillStatus ?? GapFillStatusEnum.none);
    }
  }, [gmdFlyoutResponse]);

  if (isError) {
    return <QESAlert type="danger" persist>We encountered an error retrieving information for this gap. Please try again later.</QESAlert>;
  }

  if (isLoading) {
    return <QesIcon iconName={QesIcons.LoadingV1} description="loading" className="qes-icon-spin" color={QesColors.QesTertiaryGreen2} style={{ fontSize: "1.5rem" }} />;
  }

  return (
    <div className="gmd-gap-flyout padding-zero">
      <header>
        {priorityBadge} {fillabilityBadge} {typeBadge}
        <h1 className="gmd-gap-flyout__title">{gmdFlyoutResponse.countyName}, {gmdFlyoutResponse.countyState}</h1>
        <p className="gmd-gap-flyout__subtitle">{props.specialty}</p>
      </header>

      <div className="gmd-gap-flyout__resolution-status-row">
        <div className={`faux-label`}>Resolution Status</div>
        <span className={`gmd-gap-flyout__resolution-status ${resolutionStatusColorRecord[tempResolutionStatus]}`}>{presentableGapFillStatusRecord[tempResolutionStatus]}</span>
        <NewActionMenu
          buttonText=""
          aria-description="click to toggle resolution status options menu"
          testId="gmd-gap-flyout__resolution-status"
          buttonClassNames="gmd-gap-flyout__resolution-status-button"
          closeOnClick
        >
          <div className="faux-label gmd-gap-flyout__resolution-status-menu-header">Status</div>
          {resolutionStatusActionMenuItems}
        </NewActionMenu>
      </div>

      <div className="gmd-gap-flyout__gap-time-row">
        <div className="gmd-gap-flyout__gap-age">
          {gmdFlyoutResponse.gapAgeDays !== undefined ? (
            <>
              <span className="faux-label">Gap Age</span>
              <span className="gmd-gap-flyout__gap-time-value">{gmdFlyoutResponse.gapAgeDays.toLocaleString()} days</span>
            </>
          ) : null}
        </div>
        <div className="gmd-gap-flyout__due-date">
          <span className="faux-label">Due Date</span>
          <span className="gmd-gap-flyout__gap-time-value">{tempDateDue || "---"}
            <QESButton qesType="none" qesTestId="gmd-gap-flyout__due-date" className="gmd-gap-flyout__due-date-button" onClick={() => { dateInputRef.current.showPicker(); }}>
              <QesIcon iconName={QesIcons.Calendar} description="toggle date picker" />
              <QESDateInput
                label=""
                hideLabel
                testId="gmd-gap-flyout__due-date"
                qesRef={dateInputRef}
                onChange={(e) => { setTempDateDue(formatDate(new Date(e.target.value))) }}
              />
            </QESButton>
          </span>
        </div>
      </div>

      <div className="gmd-gap-flyout__adequacy-row">
        <span className="faux-label">Coverage Score</span>
        <div className="gmd-gap-flyout__progress">
          <div className="gmd-gap-flyout__progress-piece" style={{ width: `${coverageScore}%` }} />
        </div>
        <div className="gmd-gap-flyout__adequacy-details">
          <span>
            <span className="demibold">Current score:</span>
            <span className="text-danger"> {coverageScore}%</span>
            {" "}/ 100% to pass
          </span>
          <span>Market potential: {marketPotential}%</span>
        </div>
      </div>

      <div className="gmd-gap-flyout__provider-counts-row">
        <div className="gmd-gap-flyout__provider-count">
          <span className="gmd-gap-flyout__provider-count-value">{gmdFlyoutResponse.contractedProviders.length.toLocaleString()}</span>
          <span className="faux-label">Network Servicing</span>
        </div>
        <div className="gmd-gap-flyout__provider-count">
          <span className="gmd-gap-flyout__provider-count-value">{gmdFlyoutResponse.availableProviders.length.toLocaleString()}</span>
          <span className="faux-label">Prospect/Market</span>
        </div>
      </div>

      <div className="gmd-gap-flyout__provider-views">
        <div className="gmd-gap-flyout__provider-view-toggles">
          <QESButton
            qesType="secondary"
            className={providersView === ProvidersView.Network ? "active" : ""}
            onClick={() => setProvidersView(ProvidersView.Network)}
            qesTestId="gmd-gap-flyout__providers-network"
          >
            Network Servicing ({gmdFlyoutResponse.contractedProviders.length.toLocaleString()})
          </QESButton>
          <QESButton
            qesType="secondary"
            className={providersView === ProvidersView.Impact ? "active" : ""}
            onClick={() => setProvidersView(ProvidersView.Impact)}
            qesTestId="gmd-gap-flyout__providers-impact"
          >
            Prospect/Market ({gmdFlyoutResponse.availableProviders.length.toLocaleString()})
          </QESButton>
        </div>
      </div>

      <div className="gmd-gap-flyout__table">
        {providersView === ProvidersView.Network && (
          // gmdFlyoutResponse.availableProviders.map((c) => c.name)
          <Waffles<GmdProvidersTable>
            columns={providersColumnConfig}
            customSort={customSort}
            data={networkProvidersData}
            disableStickyHeader
            fullWidth
            testId="gmd-network-providers"
          />
        )}

        {providersView === ProvidersView.Impact && (
          // impactProviders.map((a) => a.name)
          <Waffles<GmdProvidersTable>
            columns={providersColumnConfig}
            customSort={customSort}
            data={impactProvidersData}
            disableStickyHeader
            fullWidth
            testId="gmd-impact-providers"
          />
        )}
      </div>

      <div className="gmd-gap-flyout__footer">
        {/* <Link
          data-testid="gmd-gap-flyout__view-in-provider-hub"
          target="_blank"
          rel="noreferrer"
          to={`/projects/${props.projectId}/provider-hub`}
          className={`qes-button ${QESButtonTypeClasses.get("outline")}`}
        >
          View in Provider Hub <QesIcon iconName={QesIcons.ShareV4} description="click to open new tab" />
        </Link> */}
        <QESButton qesType="primary" qesTestId="gmd-gap-flyout__submit" className="gmd-gap-flyout__submit">Update status</QESButton>
      </div>
    </div>
  );
}